// =====================================================================
// Assessment Center — Auth module: session storage.
//
// Deliberately framework-agnostic (no React here) so plain data
// modules — src/features/student/lib/mock-api.ts,
// src/features/instructor/lib/mock-api.ts — can read "who is the
// current user" without importing React. AuthContext.tsx wraps this
// for components that need to re-render on sign-in/out.
// =====================================================================
import type { AuthUser } from "./types";

const STORAGE_KEY = "assessment-center:session";

type Listener = (user: AuthUser | null) => void;
const listeners = new Set<Listener>();

export function getSession(): AuthUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function setSession(user: AuthUser): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
  listeners.forEach((l) => l(user));
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
  listeners.forEach((l) => l(null));
}

/** Returns an unsubscribe function. */
export function onSessionChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Throws with a clear message if nobody is signed in — used by data
 *  modules that require an authenticated user (e.g. "create a question"). */
export function requireSession(expectedRole?: AuthUser["role"]): AuthUser {
  const user = getSession();
  if (!user) throw new Error("Not signed in.");
  if (expectedRole && user.role !== expectedRole) {
    throw new Error(`This action requires a ${expectedRole} account.`);
  }
  return user;
}
